import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { zipObject } from 'lodash-es';
import { SelectItem } from 'primeng/api';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { BaseOperation } from '../../models/thermal-operation.model';
import { AnnularResultsUi, DetailedAnnularResultsPhase } from '../../models/thermal-results.model';
import { ThermalOperationsService } from '../../services/thermal-operations.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { forkJoin } from 'rxjs';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { UIEntitiesRef, updateEntities, withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';

@Component({
  selector: 'app-detailed-annular-results',
  templateUrl: './detailed-annular-results.component.html',
  standalone: false
})
export class DetailedAnnularResultsComponent implements OnInit, OnDestroy {

  public isLoading = signal<boolean>(true);
  public missingOperations = false;
  public needToCalculate: boolean;
  public results: any[];
  public allOperationsResults: any[];
  public calculationError = "Please calculate PERICAL to see results.";
  public resultTypesDropdown: { header: string, field: number, valueFormatter: any, value: number }[] = [];
  public multipleResultHeaders: { header: string, field: number, valueFormatter: any, value: number }[] = [];
  public xAxisTitle = '';
  public numDecimalPoints: number;
  public plotName: string;
  public xAxisRange: number[] = null;
  public resultsNotUpToDate = false;

  // Multiple
  public columnDefinitions: any[] = [];
  public multipleResultsFull: any[] = [];
  public annulusNames: { value: string, label: string }[] = [];

  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];

  public fluidGasDisplay: SelectItem[] = [
    { label: 'Fluid/Mixture', value: 'fluid' },
    { label: 'Liquid & Gas Fractions', value: 'gas' }
  ];

  public singleMultiple: SelectItem[] = [
    { label: 'Single Result', value: 'singleAnnulus' },
    { label: 'Single Annulus', value: 'multipleAnnuli' }
  ];

  public operations: Partial<BaseOperation>[];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public resultsGrid: any[];
  public cols: any[];
  public phases: DetailedAnnularResultsPhase[];
  public temperatureUnit: string;
  public depthUnit: string;
  public shortLengthUnit = 'in';
  public readonly formatAnnulusOption = (option: { label: string }): string => {
    const unit = this.shortLengthUnit;
    return option.label.replace(/\d+\.\d+/g, value =>
      formatUnitNumber(Number(value), unit, undefined, undefined, { [unit]: unit === 'in' ? 3 : 2 })
    );
  };
  public pressureUnit: string;
  public densityUnit: string;
  public gasGradientUnit: string;
  public speedUnit: string;
  public specificHeatCapacityUnit: string;
  public enthalpyUnit: string;
  public heatUnit: string;
  public thermalConductivityUnit: string;
  public heatTransferCoefficientUnit: string;
  public expansivityUnit: string;
  public yieldPointUnit: string;
  public pvUnit: string;
  public massFlowUnit: string;

  // State Management
  private _componentId: string;
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.annularResultsStore = this._storeService.createStore(this.componentId, new AnnularResultsUi, withUIEntities<XyLinePlotUi>());
    this.selectedSingleMultiple = this.annularResultsStore.state.selectedSingleMultiple || 'singleAnnulus';
    this.selectedResultType = this.annularResultsStore.state.selectedResultType;
    this.selectedAnnulus = this.annularResultsStore.state.selectedAnnulus;
    this.selectedFluidGas = this.annularResultsStore.state.selectedFluidGas || 'fluid';
    this.selectedResultsDisplay = this.annularResultsStore.state.selectedResultsDisplay || 'plot';
    this.selectedDepthView = this.annularResultsStore.state.selectedDepthView || 'md';
    this.selectedOperation = this.annularResultsStore.state.selectedOperation;
    this.selectedPhase = this.annularResultsStore.state.selectedPhase;
  }
  get componentId(): string {
    return this._componentId;
  }
  public annularResultsStore: Store;

  public selectedSingleMultiple: string = 'singleAnnulus';
  public selectedResultType: any;
  public selectedAnnulus: any;
  public selectedFluidGas: string = 'fluid';
  public selectedResultsDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public selectedOperation: Partial<BaseOperation>;
  public selectedPhase: DetailedAnnularResultsPhase;

  constructor(
    private _thermalOperationsService: ThermalOperationsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    const uu = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.temperatureUnit = uu.temperature;
    this.depthUnit = uu.longLengths;
    this.shortLengthUnit = uu.shortLengths;
    this.pressureUnit = unitsLib[uu.pressure].symbol;
    this.densityUnit = unitsLib[uu.density].symbol;
    this.gasGradientUnit = uu.gasGradient;
    this.speedUnit = uu.speed;
    this.specificHeatCapacityUnit = unitsLib[uu.specificHeatCapacity].symbol;
    this.enthalpyUnit = uu.enthalpy;
    this.heatUnit = uu.heat;
    this.thermalConductivityUnit = uu.thermalConductivity;
    this.heatTransferCoefficientUnit = uu.heatTransferCoefficient;
    this.expansivityUnit = uu.coefficientOfThermalExpansion == '1/°F' ? '1/R' : '1/K';
    this.yieldPointUnit = uu.yieldPoint;
    this.pvUnit = uu.plasticViscosity;
    this.massFlowUnit = unitsLib[uu.massFlow].symbol;
    this.getResultTypesDropdown();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getData();
  }

  private signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_THERMAL_RESULTS || data.action === PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.getData();
      if (!this.selectedResultType) {
        this.selectedResultType = this.resultTypesDropdown[0].field.toString();
        this.annularResultsStore.update(state => ({ ...state, selectedResultType: this.selectedResultType }));
      }
    }
  }

  public getData() {
    this.isLoading.set(true);
    const sources = [
      this._thermalOperationsService.getThermalOperations(),
      this._thermalOperationsService.getDetailedAnnularResults()
    ];

    forkJoin(sources).subscribe({
      next: ([operations, allResults]) => {
        this.operations = operations;
        if ((!this.selectedOperation || !operations.find(op => op.id === this.selectedOperation.id)) && this.operations.length > 0) {
          this.selectedOperation = this.operations[0];
          this.annularResultsStore.update(state => ({ ...state, selectedOperation: this.selectedOperation }));
        }

        this.allOperationsResults = allResults;

        if (!this.selectedResultType) {
          this.selectedResultType = this.resultTypesDropdown[0].value;
          this.annularResultsStore.update(state => ({ ...state, selectedResultType: this.selectedResultType }));
        }

        this.resultsNotUpToDate = allResults.length > 0 ? allResults[0].phases[0].annulusResultsTables.length < 46 : false;

        if (this.allOperationsResults.length > 0) {
          const selectedOperation = this.allOperationsResults.find(x => x.operationId === this.selectedOperation.id);
          this.selectedPhase = selectedOperation?.phases[selectedOperation.phases.length - 1];
          this.annularResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
          if (this.selectedSingleMultiple == 'singleAnnulus') {
            if (!this.selectedResultsDisplay) {
              this.selectedResultsDisplay = 'plot';
              this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: 'plot' }));
            }
            this.remapResults();
          } else if (this.selectedSingleMultiple == 'multipleAnnuli') {
            this.selectedResultsDisplay = 'grid';
            this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: 'grid' }));
            this.remapMultipleResults();
          }
        } else {
          this.results = [];
          this.cols = [];
          this.resultsGrid = [];
          this.multipleResultsFull = [];
          this.needToCalculate = true;
        }

        this.missingOperations = this.operations.length === 0;
        if (this.missingOperations) {
          this.needToCalculate = false;
          this.results = [];
        }
        this.plotName = this.setPlotName();

        this.isLoading.set(false);
      },
      error: () => {
        this.missingOperations = true;
        this.isLoading.set(false);
      }
    });
  }

  setPlotName(): string {
    if (this.selectedSingleMultiple === 'singleAnnulus') {
      return 'detailedAnnularResultsPlot' + ' - ' + this.selectedResultType + ' ' +
        this.selectedOperation?.id + ' ' + this.selectedPhase?.phaseNumber +
        this.selectedAnnulus?.label;
    } else {
      return 'detailedAnnularResultsPlot' + ' - ' + this.selectedResultType + ' ' +
        this.selectedOperation?.id + ' ' + this.selectedPhase?.phaseNumber;
    }
  }

  public dropdownChange(e) {
    if (this.selectedSingleMultiple === 'singleAnnulus') {
      this.selectedResultType = e.value;
      this.annularResultsStore.update(state => ({ ...state, selectedResultType: e.value }));
      this.plotName = this.setPlotName();
      this.annularResultsStore.update(updateEntities(this.plotName,
        {
          xAxisRange: (e.value == 6 || e.value == 7) ?? [0, 1]
        },
        { ref: UIEntitiesRef }
      ));
      // this.xAxisRange = e.value == 6 || e.value == 7 ? [0, 1] : null;
      this.remapResults();
    } else {
      this.selectedAnnulus = e.value;
      this.annularResultsStore.update(state => ({ ...state, selectedAnnulus: e.value }));
      this.plotName = this.setPlotName();
      this.remapMultipleResults();
    }

  }

  public modeToggle(e) {
    this.selectedSingleMultiple = e.value;
    this.annularResultsStore.update(state => ({ ...state, selectedSingleMultiple: e.value }));
    if (e.value == 'singleAnnulus') {
      this.selectedResultsDisplay = 'plot';
      this.selectedResultType = this.selectedResultType ?? this.resultTypesDropdown[0].value;
      this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: 'plot', selectedResultType: this.selectedResultType }));
      this.plotName = this.setPlotName();
      this.remapResults();
    } else if (e.value == 'multipleAnnuli') {
      this.selectedResultsDisplay = 'grid';
      this.selectedAnnulus = this.selectedAnnulus ?? this.annulusNames[0];
      this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: 'grid', selectedAnnulus: this.selectedAnnulus }));
      this.plotName = this.setPlotName();
      this.remapMultipleResults();
    }
  }

  public onSelectedResultsDisplayUpdated(e): void {
    this.selectedResultsDisplay = e.value;
    this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: e.value }));
    this.plotName = this.setPlotName();
    this.remapResults();
  }

  public onSelectFluidGas(e): void {
    this.selectedFluidGas = e.value;
    this.annularResultsStore.update(state => ({ ...state, selectedFluidGas: e.value }));
    this.plotName = this.setPlotName();
    this.remapMultipleResults();
  }

  private remapMultipleResults(): void {
    const selectedOperationResults = this.allOperationsResults.find(o => o.operationId === this.selectedOperation.id);
    this.needToCalculate = !selectedOperationResults;
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (!this.selectedPhase) {
      this.selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
      this.annularResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
      this.plotName = this.setPlotName();
    }

    const annResultsTables = this.selectedPhase?.annulusResultsTables;
    this.annulusNames = annResultsTables[0].headers.slice(2).map(h => {
      const cleanLabel = h
        .replace(/"/g, '\u2033'); // Replace double quotes with ″ (prime symbol)

      return {
        value: toCamelCaseString(h),
        label: cleanLabel
      };
    });

    this.multipleResultsFull = [];
    const start = this.selectedFluidGas === 'fluid' ? 0 : 29;
    const end = this.selectedFluidGas === 'fluid' ? 31 : annResultsTables['length'];
    const selectedPhaseLength = annResultsTables[0].rows.length;
    for (let l = 0; l < selectedPhaseLength; l++) {
      const multipleResults = [];
      for (let i = start; i < end; i++) {
        if (i === start) {
          multipleResults.push(annResultsTables[i].rows[l][0]);
          multipleResults.push(annResultsTables[i].rows[l][1]);
        }
        let annulusColumnIndex = annResultsTables[i].headers.findIndex(x => toCamelCaseString(x) == this.selectedAnnulus?.value);
        if (annulusColumnIndex < 0) {
          annulusColumnIndex = 2;
        }
        multipleResults.push(annResultsTables[i].rows[l][annulusColumnIndex]);
      }

      this.multipleResultsFull.push(multipleResults);
    }

    this.multipleResultHeaders = this.getResultTypes();

    if (this.selectedFluidGas === 'gas') {
      this.multipleResultHeaders.splice(2, 29);
    } else {
      this.multipleResultHeaders.splice(31, end);
    }

    this.resultsGrid = this.multipleResultsFull.map(r => zipObject(this.multipleResultHeaders.map(c => c.field), r));
  }

  private remapResults(): void {
    if (this.resultTypesDropdown[0].header.includes('MD') || this.resultTypesDropdown[0].header.includes('TVD')) {
      this.resultTypesDropdown.splice(0, 2);
    }

    const selectedOperationResults = this.allOperationsResults.find(o => o.operationId === this.selectedOperation.id);
    this.needToCalculate = !selectedOperationResults;
    this.phases = selectedOperationResults ? selectedOperationResults.phases : null;
    if (this.selectedPhase == undefined || !this.selectedPhase) {
      this.selectedPhase = this.phases ? this.phases[this.phases.length - 1] : null;
      this.annularResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
      this.plotName = this.setPlotName();
    }

    if (this.selectedResultType === 6 || this.selectedResultType === 7) {
      this.selectedResultsDisplay = 'grid';
      this.annularResultsStore.update(state => ({ ...state, selectedResultsDisplay: 'grid' }));
    }

    if (this.selectedPhase) {
      this.results = this.selectedPhase.annulusResultsTables[this.selectedResultType].rows;
      if (isNaN(this.results[0][3]) && !this.results[0][3]) {
        this.results = [];
        this.cols = [];
        this.resultsGrid = [];
        this.needToCalculate = true;
        // this.calculationError = selectedOperationResults['errors'][1] ?? "PERICAL engine error while calculating operation.";
        this.calculationError = "PERICAL engine error while calculating operation.";
        return;
      }

      this.xAxisTitle = this.resultTypesDropdown[this.selectedResultType].header;

      this.cols = this.selectedPhase.annulusResultsTables[this.selectedResultType].headers.map(h => {
        return {
          field: toCamelCaseString((h === `MD (${this.depthUnit})` || h === `TVD (${this.depthUnit})`) ? h.split(' ').shift() : h),
          header: h,
          valueFormatter: (h === `MD (${this.depthUnit})` || h === `TVD (${this.depthUnit})`) ? (params: any) => this.formatDecimal(params, 100) : this.selectedResultType === 5 ? (params: any) => params : (params: any) => this.formatDecimalNew(params, 3)
        };
      });

      this.resultsGrid = this.results.map(r => zipObject(this.cols.map(c => c.field), r));

    } else {
      // this.calculationError = selectedOperationResults['errors'][1] ?? (!this.needToCalculate ? "PERICAL engine error while calculating operation." : "Please calculate PERICAL to see results.");
      this.calculationError = !this.needToCalculate ? "PERICAL engine error while calculating operation." : "Please calculate PERICAL to see results.";
      this.needToCalculate = true;
      this.results = [];
      this.cols = [];
      this.resultsGrid = [];
      this.isLoading.set(false);
    }
  }

  private formatDecimal(value: any, divisor: number): string {
    if (value === null || value === undefined || isNaN(value)) {
      return null;
    }

    const maxFractionDigits = Math.max(0, Math.round(Math.log10(divisor)));
    const roundedValue = Number(Number(value).toFixed(maxFractionDigits));

    return roundedValue.toLocaleString('en-US', {
      maximumFractionDigits: maxFractionDigits
    });
  }

  private formatDecimalNew(value: any, numDecimalPoints: number): string {
    if (isNaN(value)) {
      return value;
    }

    const roundedValue = Number(Number(value).toFixed(numDecimalPoints));
    return roundedValue.toLocaleString('en-US', {
      maximumFractionDigits: numDecimalPoints,
      useGrouping: false
    });
  }

  public depthViewToggle(e) {
    this.selectedDepthView = e.value;
    this.annularResultsStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  public onOperationSelected(e): void {
    this.selectedOperation = e.value;
    this.annularResultsStore.update(state => ({ ...state, selectedOperation: e.value }));
    if (this.allOperationsResults.length > 0) {
      if (this.selectedSingleMultiple === 'singleAnnulus') {
        const firstOperation = this.allOperationsResults.find(x => x.operationId === e.value.id);
        this.selectedPhase = firstOperation?.phases[firstOperation.phases.length - 1];
        this.annularResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
        this.remapResults();
      } else {
        const selectedOperation = this.allOperationsResults.find(x => x.operationId === e.value.id);
        this.selectedPhase = selectedOperation?.phases[selectedOperation.phases.length - 1];
        this.annularResultsStore.update(state => ({ ...state, selectedPhase: this.selectedPhase }));
        this.annularResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'multipleAnnuli' }));
        this.remapMultipleResults();
      }
    } else {
      this.needToCalculate = true;
    }
    this.plotName = this.setPlotName();
  }

  public onPhaseSelected(results: any[]) {
    if (this.allOperationsResults.length > 0) {
      if (this.selectedSingleMultiple === 'singleAnnulus') {
        this.selectedPhase = results['value'];
        this.annularResultsStore.update(state => ({ ...state, selectedPhase: results['value'] }));
        this.remapResults();
      } else {
        this.selectedSingleMultiple = 'multipleAnnuli';
        this.selectedPhase = results['value'];
        this.annularResultsStore.update(state => ({ ...state, selectedSingleMultiple: 'multipleAnnuli' }));
        this.annularResultsStore.update(state => ({ ...state, selectedPhase: results['value'] }));
        this.remapMultipleResults();
      }
    } else {
      this.needToCalculate = true;
    }
    this.plotName = this.setPlotName();
  }

  private getResultTypes() {
    const resultTypes = [
      { header: `MD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `TVD (${this.depthUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Pressure (${this.pressureUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Velocity (${this.speedUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Density (${this.densityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Viscosity (${this.pvUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Yield Point (${this.yieldPointUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Mass Flow Rate (${this.massFlowUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: 'Flow Regime', valueFormatter: (params: any) => params },
      { header: 'Flow Pattern', valueFormatter: (params: any) => params },
      { header: 'Quality', valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: 'Holdup', valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: 'C-Factor (((lbm/ft)^0.5)/s)', valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Vaporization Enthalpy (${this.enthalpyUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Heat Generation (${this.heatUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Latent Heat Term (${this.heatUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Pressure Loss (${this.gasGradientUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Frictional Pressure Loss (${this.gasGradientUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Accelerational Pressure Loss (${this.gasGradientUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: 'Reynolds Number', valueFormatter: (params: any) => Math.round(params) },
      { header: 'Fanning FF', valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: 'Prandtl Number', valueFormatter: (params: any) => this.formatDecimalNew(params, 3) },
      { header: `Convection Coeff (${this.heatTransferCoefficientUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Natural Convection Coeff (${this.heatTransferCoefficientUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Radiation Coeff (${this.heatTransferCoefficientUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: 'Nusselt Number', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: 'Grashof Number', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Specific Heat Cp (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Specific Heat Cv (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Fluid Effective Conductivity (${this.thermalConductivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Fluid Expansivity (${this.expansivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Velocity (${this.speedUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Density (${this.densityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Viscosity (${this.pvUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Specific Heat Cp (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Specific Heat Cv (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Thermal Conductivity (${this.thermalConductivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Liquid Expansion Coeff (${this.expansivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: 'Liquid Dissolved GOR (SCF/bbl)', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: 'Liquid Water Holdup (%)', valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Liquid Water Fraction Density (${this.densityUnit})`, valueFormatter: (params: any) => this.formatDecimal(params, 100) },
      { header: `Gas Velocity (${this.speedUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Density (${this.densityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Viscosity (${this.pvUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Specific Heat Cp (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Specific Heat Cv (${this.specificHeatCapacityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Thermal Conductivity (${this.thermalConductivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) },
      { header: `Gas Expansion Coeff (${this.expansivityUnit})`, valueFormatter: (params: any) => this.formatDecimalNew(params, 4) }
    ];

    return resultTypes.map((item, index) => ({
      ...item,
      field: index,
      value: index < 2 ? 999 : index - 2
    }));
  }

  private getResultTypesDropdown(): void {
    this.resultTypesDropdown = this.getResultTypes();
    this.resultTypesDropdown.splice(0, 2);
  }

  ngOnDestroy() {
    this.signalRfunc = null;
  }
}
